/**
 * #2072 — platform-wide notice (entry modal + top banner).
 * Everything lives under /users/platform-notice; admins read the stored value
 * through the PLATFORM_NOTICE key of /users/admin/settings.
 */
import { apiClient } from './client'

export const PLATFORM_NOTICE_SETTING_KEY = 'PLATFORM_NOTICE'

export type PlatformNoticeLanguage = 'en' | 'he'

/** A null text means "use the default copy from the locale files". */
export interface PlatformNoticeTexts {
  title: string | null
  message: string | null
  banner: string | null
}

export interface PlatformNotice {
  noticeId: string
  texts: Record<PlatformNoticeLanguage, PlatformNoticeTexts>
}

export interface PlatformNoticeState {
  notice: PlatformNotice | null
  acknowledged: boolean
  requiresAcknowledgement: boolean
}

/** The PLATFORM_NOTICE value an admin saves (validated by user-service). */
export interface PlatformNoticeConfig {
  active: boolean
  noticeId: string
  startsAt: string | null
  endsAt: string | null
  texts: Record<PlatformNoticeLanguage, Record<keyof PlatformNoticeTexts, string>>
}

const NO_NOTICE: PlatformNoticeState = { notice: null, acknowledged: false, requiresAcknowledgement: false }

export const platformNoticeApi = {
  // apiClient already returns the { success, data } envelope: unwrap once.
  get: async (): Promise<PlatformNoticeState> => {
    const res = await apiClient.get<PlatformNoticeState>('/users/platform-notice')
    return res.data ?? NO_NOTICE
  },

  acknowledge: async (noticeId: string): Promise<void> => {
    await apiClient.post(`/users/platform-notice/${encodeURIComponent(noticeId)}/acknowledge`)
  },

  // Admin only. Not PUT /users/admin/settings/PLATFORM_NOTICE: user-service's
  // global input sanitizer rewrites `body.value` (it deletes words such as
  // "update" and characters such as ' ( ) &); `body.platformNotice` is exempt.
  save: async (config: PlatformNoticeConfig): Promise<void> => {
    await apiClient.put('/users/platform-notice', { platformNotice: config })
  },
}
